import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Icon, Tooltip } from "@chakra-ui/react";
import parseTweet from "twitter-text/dist/parseTweet";

const wrapperStyles = () => ({
  display: "inline-flex",
  alignItems: "center",
  verticalAlign: "middle",
  borderRadius: "sm",
  paddingTop: "0.5",
  paddingBottom: "0.5",
  paddingLeft: "1",
  paddingRight: "1",
  color: "gray.200",
  fontSize: "sm",
  lineHeight: "1",
});

const iconStyles = () => ({
  marginRight: "0.5",
  lineHeight: "1",
  fontSize: "0.9em",
});

const textStyles = (error) => ({
  color: error ? "red.500" : null,
  lineHeight: "1.3",
});

const normalizeText = (text) => {
  const normalizedText =
    typeof String.prototype.normalize === "function" ? text.normalize() : text;
  return normalizedText.replace(/\r\n/g, "\n");
};

export const countChars = (state) => normalizeText(state?.message || "").length;
export const countCharsTwitter = (state) =>
  parseTweet(state?.message || "").weightedLength;
export const countHashtags = (state) =>
  (state?.message?.match(/(^|\s)(#[\w\p{L}]+)/giu) || []).length;
export const countAtMentions = (state) =>
  (state?.message?.match(/(^|\s)(@[\w\p{L}]+)/giu) || []).length;
export const countMentions = (state) =>
  state?.entitiesMap?.filter((item) => item.type === "mention").length || 0;

export const AdvancedMessageCounter = ({
  length,
  limit,
  leftIcon,
  ...props
}) => {
  const error = limit && length > limit;
  const title = limit ? `${length}/${limit}` : null;

  return (
    <Tooltip placement="top" hasArrow label={title} isDisabled={!title}>
      <Box sx={wrapperStyles(error)} {...props}>
        {leftIcon ? <Icon sx={iconStyles(error)} as={leftIcon} /> : null}
        <Box sx={textStyles(error)}>{length}</Box>
      </Box>
    </Tooltip>
  );
};

AdvancedMessageCounter.propTypes = {
  length: PropTypes.number.isRequired,
  limit: PropTypes.number,
  leftIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

AdvancedMessageCounter.defaultProps = {
  limit: undefined,
  leftIcon: undefined,
};

export const AdvancedMessageCharsCounter = ({ state, ...props }) => {
  const length = useMemo(() => countChars(state), [state?.message, countChars]);

  return <AdvancedMessageCounter length={length} {...props} />;
};

AdvancedMessageCharsCounter.propTypes = {
  state: PropTypes.shape({
    message: PropTypes.string,
  }).isRequired,
};

export const AdvancedMessageCharsTwitterCounter = ({ state, ...props }) => {
  const length = useMemo(
    () => countCharsTwitter(state),
    [state?.message, countCharsTwitter]
  );

  return <AdvancedMessageCounter length={length} {...props} />;
};

AdvancedMessageCharsTwitterCounter.propTypes = {
  state: PropTypes.shape({
    message: PropTypes.string,
  }).isRequired,
};

export const AdvancedMessageHashtagsCounter = ({ state, ...props }) => {
  const length = useMemo(
    () => countHashtags(state),
    [state?.message, countHashtags]
  );

  return <AdvancedMessageCounter length={length} {...props} />;
};

AdvancedMessageHashtagsCounter.propTypes = {
  state: PropTypes.shape({
    message: PropTypes.string,
  }).isRequired,
};

export const AdvancedMessageAtMentionsCounter = ({ state, ...props }) => {
  const length = useMemo(
    () => countAtMentions(state),
    [state?.message, countAtMentions]
  );

  return <AdvancedMessageCounter length={length} {...props} />;
};

AdvancedMessageAtMentionsCounter.propTypes = {
  state: PropTypes.shape({
    message: PropTypes.string,
  }).isRequired,
};

export const AdvancedMessageMentionsCounter = ({ state, ...props }) => {
  const length = useMemo(
    () => countMentions(state),
    [state?.entitiesMap, countMentions]
  );

  return <AdvancedMessageCounter length={length} {...props} />;
};

AdvancedMessageMentionsCounter.propTypes = {
  state: PropTypes.shape({
    entitiesMap: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
};
